import { useState, useEffect, useCallback } from "react";
import { useEntityOperations } from "./useEntityOperations";
import { FinancialService } from "../services/financialService";
import {
  UserFinancialInfo,
  UserTrialsData,
  GrantTrialInput,
} from "../types/financial";

export const useUserFinancialInfo = (userId: number | null) => {
  const [financialInfo, setFinancialInfo] = useState<UserFinancialInfo | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFinancialInfo = useCallback(async () => {
    if (!userId) {
      setFinancialInfo(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await FinancialService.getUserFinancialInfo(userId);

      if (result.success && result.data) {
        setFinancialInfo(result.data);
      } else {
        setError(result.error || "Failed to fetch financial information");
      }
    } catch (err: any) {
      setError(err.message || "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchFinancialInfo();
  }, [fetchFinancialInfo]);

  const refresh = useCallback(() => {
    fetchFinancialInfo();
  }, [fetchFinancialInfo]);

  return {
    financialInfo,
    loading,
    error,
    refresh,
  };
};

// Hook for managing user trial information
export const useUserTrials = (userId: number | null) => {
  const [trialsData, setTrialsData] = useState<UserTrialsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTrials = useCallback(async () => {
    if (!userId) {
      setTrialsData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await FinancialService.getUserTrials(userId);

      if (result.success && result.data) {
        setTrialsData(result.data);
      } else {
        setError(result.error || "Failed to fetch trial information");
      }
    } catch (err: any) {
      setError(err.message || "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchTrials();
  }, [fetchTrials]);

  const refresh = useCallback(() => {
    fetchTrials();
  }, [fetchTrials]);

  return {
    trialsData,
    activeTrial: trialsData?.active_trial || null,
    trialHistory: trialsData?.trial_history || [],
    loading,
    error,
    refresh,
  };
};

// Hook for trial operations (grant/revoke)
export const useTrialOperations = () => {
  const { run, runBoolean, ...rest } = useEntityOperations();

  const grantTrial = useCallback(
    (input: GrantTrialInput) =>
      run(
        () => FinancialService.grantTrial(input),
        "Failed to grant trial access"
      ),
    [run]
  );

  const revokeTrial = useCallback(
    (trialId: number) =>
      runBoolean(
        () => FinancialService.revokeTrial(trialId),
        "Failed to revoke trial access"
      ),
    [runBoolean]
  );

  return { grantTrial, revokeTrial, ...rest };
};
